import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { newsAPI, type News as NewsArticle } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowLeft,
  Calendar,
  User,
  Eye,
  ExternalLink,
  Star,
  Pin,
  Edit,
  Trash2,
  AlertCircle,
  Loader2,
  Mail,
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import EmailCustomersModal from '../components/news/EmailCustomersModal';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const isHelixAdmin = user?.company_type === 'helix';
  const canManageNews = user?.is_staff || user?.is_superuser || user?.company_type === 'helix';


  useEffect(() => {
    if (id) {
      loadArticle(parseInt(id));
    }
  }, [id]);

  const loadArticle = async (articleId: number) => {
    try {
      setLoading(true);
      const response = await newsAPI.getNewsArticle(articleId);
      setArticle(response.data);
      setError(null);
    } catch (err: unknown) {
      console.error('Error loading article:', err);
      setError('Failed to load article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!article) return;
    const ok = await confirm({ title: 'Delete Article', description: 'Are you sure you want to delete this article?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) {
      return;
    }

    try {
      await newsAPI.deleteNews(article.id);
      navigate('/news');
    } catch (err: unknown) {
      console.error('Error deleting article:', err);
      setError('Failed to delete article. Please try again.');
    }
  };

  const getPriorityBadge = (priority: string) => {
    if (priority === 'normal') return null;
    const styles: { [key: string]: string } = {
      urgent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      low: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    };
    return (
      <Badge className={styles[priority]}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || 'Article not found'}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/news')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to News
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/news')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to News
        </Button>
        {isHelixAdmin && (
          <div className="flex items-center gap-2">
            {!article.is_published && (
              <Button variant="outline" onClick={() => navigate(`/news/edit/${article.id}`)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => setEmailModalOpen(true)}
            >
              <Mail className="h-4 w-4 mr-2" />
              Email Selected Customers
            </Button>
          </div>
        )}
      </div>

      {/* Article Card */}
      <Card>
        <CardHeader>
          {/* Featured Image */}
          {article.image_url && (
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-64 object-cover rounded-lg mb-4"
            />
          )}

          {/* Badges */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {article.is_pinned && (
              <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                <Pin className="h-3 w-3 mr-1" />
                Pinned
              </Badge>
            )}
            {article.is_featured && (
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                <Star className="h-3 w-3 mr-1" />
                Featured
              </Badge>
            )}
            {article.category_name && (
              <Badge
                style={{
                  backgroundColor: article.category_color + '20',
                  color: article.category_color,
                  borderColor: article.category_color,
                }}
                className="border"
              >
                {article.category_name}
              </Badge>
            )}
            {getPriorityBadge(article.priority)}
          </div>

          {/* Title */}
          <CardTitle className="text-3xl mb-4">{article.title}</CardTitle>

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground border-b pb-4">
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" />
              {article.author_display}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {article.published_at
                ? format(new Date(article.published_at), 'MMMM d, yyyy')
                : format(new Date(article.created_at), 'MMMM d, yyyy')}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {article.view_count} views
            </span>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Summary */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <p className="text-lg font-medium">{article.summary}</p>
          </div>

          {/* Content */}
          <div
            className="rich-text-content max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
          />

          {/* External Link */}
          {article.external_link && (
            <div className="border-t pt-4">
              <a
                href={article.external_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                View Related Documentation
              </a>
            </div>
          )}

          {/* Read Count */}
          <div className="border-t pt-4 text-sm text-muted-foreground">
            <p>
              This article has been read by {article.read_count}{' '}
              {article.read_count === 1 ? 'person' : 'people'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email Customers Modal */}
      {canManageNews && article && id && (
        <EmailCustomersModal
          newsId={parseInt(id)}
          newsTitle={article.title}
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
        />
      )}
    </div>
  );
}
